import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import vueRouter from '@/router/index';
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import { FilePondFile } from 'filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import { ErrorTableType } from "@/shared/type";
import HttpStatus from 'http-status-codes';
import MessageConst from '@/shared/message-const';
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    selectCreation: string;                                 // 処理選択
    visibleTree: boolean;                                   // ファイル形式説明切り替え
    validateValues: ValidateType;                           // 取得したバリデーションの値
    basicFileErrorMessage: string;                          // 基本ファイルのバリデーションメッセージ文字列
    isBasicFileValid: boolean;                              // 基本ファイルのバリデーション結果
    attachmentFileErrorMessage: string;                     // 添付ファイルのバリデーションメッセージ文字列
    isAttachmentFileValid: boolean;                         // 添付ファイルのバリデーション結果
    showErrorMessageDialog: boolean;                        // エラーメッセージダイアログ表示切り替え
    fileParamSetting: string;                               // 基本、添付データファイルエラーメッセージ切り替え
    fileErrorItems: ErrorTableType[];                       // バリデーションエラーのデータ表示内容
    isClear: boolean;                                       // 入力欄のクリア中
};

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                  // パラメーター名
    value: string;                                          // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 重要情報のお知らせ：送付ファイル処理選択
 *
 * 送付ファイル処理選択画面を表示する。
 */
export default defineComponent({
    components: { FilePond, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                   // メッセージダイアログ
        const basicPondRef = ref<any>(null);                // FilePond（基本ファイル）への参照
        const attachmentPondRef = ref<any>(null);           // FilePond（添付ファイル）への参照
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            selectCreation: '1',
            visibleTree: false,
            validateValues: {},
            basicFileErrorMessage: '',
            isBasicFileValid: true,
            attachmentFileErrorMessage: '',
            isAttachmentFileValid: true,
            showErrorMessageDialog: false,
            fileParamSetting: '1',
            fileErrorItems: [],
            isClear: false
        });
        let errors: string[] = [];
        const router = vueRouter;
        const stepper = <number>1;                          // ステッパーの1段階目
        const selectCreation = {                            // 処理選択ラジオボタン
            manual: '1',                                    // 新規作成
            readFile: '2'                                   // ファイルから設定
        };
        const attachmentTreeFormat = [                      // 添付データファイル説明
            {
                name: 'ルートフォルダ',
                children: [
                    {
                        name: 'フォルダ（フォルダ名には基本データファイルで設定した組織ID / 所属コードの値を設定してください）',
                        children: [
                            { files: 'file', name: '添付ファイル1（対象組織に送付したいファイルを設定してください）' },
                            { files: 'file', name: '添付ファイル2' },
                            { files: 'file', name: '添付ファイル3' }
                        ]
                    }
                ]
            }
        ];
        const excelFileFormat = [                           // 基本データファイル説明
            {
                column: 'A',
                name: '送付区分',
                option: false,
                explanation: '送付先となる送付区分を設定してください（半角のみ） ',
                data: [
                    {
                        dataValue: '1',
                        dataExplanation: '給与グループ'
                    },
                    {
                        dataValue: '2',
                        dataExplanation: '組織代表アカウント'
                    },
                    {
                        dataValue: '3',
                        dataExplanation: '公文書グループ'
                    }
                ]
            },
            {
                column: 'B',
                name: '組織識別',
                option: false,
                explanation: '組織ID / 所属コードに入力する区分を設定してください（半角のみ）',
                data: [
                    {
                        dataValue: '1',
                        dataExplanation: '組織ID'
                    },
                    {
                        dataValue: '2',
                        dataExplanation: '所属コード'
                    }
                ]
            },
            {
                column: 'C',
                name: '組織ID / 所属コード',
                option: false,
                explanation: '組織識別で入力した区分で値を入力してください（半角のみ）\n' +
                    '「1（組織ID）」を指定した場合\n\t\t送付する組織の組織IDを設定してください\n' +
                    '「2（所属コード）」を指定した場合\n\t\t送付する組織の所属コードを設定してください',
            },
            {
                column: 'D',
                name: '組織名',
                option: true,
                explanation: '送付先の組織名を入力してください（全角半角入力可）',
            },
            {
                column: 'E',
                name: 'タイトル',
                option: false,
                explanation: '送付する重要情報のお知らせのタイトルを入力してください（全角半角入力可）',
            },
            {
                column: 'F',
                name: 'メッセージ',
                option: false,
                explanation: '送付する重要情報のお知らせの文書内容を入力してください（全角半角入力可）',
            }
        ];

        // 基本データファイルのバリデーション
        const basicFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('basicFileCount' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = basicPondRef.value.getFiles();

                // ファイルが最低1つ登録されている
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['basicFileCount'].value),
                    Number(state.validateValues['basicFileSize'].value),
                    state.validateValues['basicFileExtension'].value,
                    '基本データ');
            }
        ];

        // 添付ファイルのバリデーション
        const attachmentFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('attachmentFileCount' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = attachmentPondRef.value.getFiles();

                // ファイルが最低1つ登録されている
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['attachmentFileCount'].value),
                    Number(state.validateValues['attachmentFileSize'].value),
                    state.validateValues['attachmentFileExtension'].value,
                    '添付データ');
            }
        ];

        /**
         * ファイルバリデーション
         * 
         * @param files 対象ファイル
         * @param cnt バリデーション値（ファイル数）
         * @param size バリデーション値（ファイルサイズ）
         * @param ext バリデーション値（拡張子）
         * @param message メッセージ（'基本データ' or '添付データ'）
         * @returns 正常の場合true エラーの場合メッセージ文字列
         */
        function validateFiles(files: FilePondFile[], cnt: number, size: number, ext: string, message: string) {
            // ファイル数が超過していないか
            if (files.length > cnt) {
                return CommonFunction.replaceArray(
                    MessageConst.E_FILE_FILE_COUNT, [
                    message,
                    cnt + '件のファイル']);
            }

            // ファイルサイズが超過または0バイトでないか
            for (const key in files) {
                if (files[key].fileSize > size) {
                    // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_MAX_SIZE, [size / 1024]);
                } else if (files[key].fileSize === 0) {
                    // ファイルサイズが0バイトの場合、エラーメッセージを返す
                    return MessageConst.E_FILE_NO_DATA;
                }
            }

            // 拡張子が登録対象か
            for (const key in files) {
                // 登録しようとするファイルの拡張子を得る
                const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                const fileExtension = parts[1];

                // m_parametersの拡張子一覧
                const validateExtension = ext.split(',');

                if (validateExtension.indexOf(fileExtension) === -1) {
                    // 比較した結果、拡張子一覧に含まれていないとき、エラーメッセージを返す
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_EXTENSION, [ext]);
                }
            }

            return true;
        }

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-send');

            // mainTitle変更
            await ctx.root.$store.commit('setMainTitle', '重要情報のお知らせ登録');

            // step1: 処理選択のファイルバリデーションのパラメータを取得する
            const response = await ctx.root.$store.dispatch('importantInformation/getSelectCreationParams');

            // レスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // バリデーションパラメータをstate.validateValuesに設定する
            state.validateValues = response.data.parameters;
        }

        /**
         * 基本ファイルへファイルを追加・削除された時のイベント
         */
        function updateBasicFiles() {
            // 基本ファイルのバリデーションを行う
            for (let index = 0; index < basicFilesRules.length; index++) {
                const rule: any = basicFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり
                    state.basicFileErrorMessage = valid;
                    state.isBasicFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.basicFileErrorMessage = '';
                    state.isBasicFileValid = true;
                }
            }
        }

        /**
         * 添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateAttachmentFiles() {
            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < attachmentFilesRules.length; index++) {
                const rule: any = attachmentFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり
                    state.attachmentFileErrorMessage = valid;
                    state.isAttachmentFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.attachmentFileErrorMessage = '';
                    state.isAttachmentFileValid = true;
                }
            }
        }

        /**
         * 「次へ」ボタンクリック処理
         */
        async function nextStep() {
            // テーブル削除APIを呼び出す
            // URL: DELETE /api/important-information/temporary
            // パラメータ: なし
            const response = await ctx.root.$store.dispatch('importantInformation/deleteTempData');

            // APIが正常に実行できなかった場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // state.selectCreationの値が「新規登録」の場合
            if (state.selectCreation === selectCreation['manual']) {
                // 組織選択へ遷移する
                router.push('/important-information/select-organization');
                return;
            } else {
                // state.selectCreationの値が「ファイルから設定する」の場合
                // バリデーションを確認する
                updateBasicFiles();
                updateAttachmentFiles();
                if (!state.isBasicFileValid || !state.isAttachmentFileValid) {
                    // エラーがある場合、処理選択画面に戻る
                    return;
                }
                // 確認メッセージダイアログ表示
                //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
                //   「いいえ」をクリックした場合はダイアログを閉じ、処理選択画面へ戻る
                let displayMessage = '';
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['送付データ設定']);
                commonRef.value.displayConfirmationMessageDialog(displayMessage);
            }
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            // 基本ファイル、添付ファイルを取得する
            // APIへ渡すためにFilePondFileオブジェクトからFileオブジェクトを作る
            const basicFiles: File[]
                = basicPondRef.value.getFiles().map((e: FilePondFile) => e.file);
            const attachmentFiles: File[]
                = attachmentPondRef.value.getFiles().map((e: FilePondFile) => e.file);

            // 処理選択のファイル内容登録処理を行う
            // POST /api/important-information/select-creation/file-data
            const response = await ctx.root.$store.dispatch('importantInformation/addSelectCreationFileData',
                {
                    basicFile: basicFiles,
                    attachmentFile: attachmentFiles
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // バリデーションチェックでエラーが発生した場合（400のステータスコード）
                    // ローダーの表示を閉じる
                    commonRef.value.state.showDialog = false;
                    // レスポンスのエラー内容を保存する
                    errors = response.data.errors;
                    // エラーダイアログ表示切替関数呼び出し
                    changeDialog();
                    return;
                } else {
                    // 400以外のステータスコードのエラーの場合
                    // エラーメッセージを表示して処理選択画面へ戻る
                    commonRef.value.errorDisplayTransitionControl(response);
                    return;
                }
            }

            // HTTPステータスコードが200の場合
            // 組織選択へ遷移する
            router.push('/important-information/select-organization');
        }

        /**
         * ダイアログ表示切替
         */
        function changeDialog(fileParam = '1') {
            // エラーダイアログを表示する
            state.showErrorMessageDialog = true;
            state.fileParamSetting = fileParam;

            // エラー表示内容を初期化する
            state.fileErrorItems = [];
            let index = 0;
            // レスポンスデータ.errorsの件数分繰り返す
            for (let i = 0; i < errors.length; i++) {
                // エラーメッセージ一件取得して「、」で分割する
                const oneError = errors[i].split('、');

                // fileParamが'1'（基本データファイル）で、分割した最初のデータが「基本」の場合
                if (fileParam === '1' && oneError[0] === SystemConst.FILE_DIVISION.BASIC) {
                    // 「、」で分割した真ん中のデータを'errorLocation'に設定する
                    // 「、」で分割した後ろのデータを'message'に設定する
                    state.fileErrorItems[index] = {errorLocation: oneError[1], message: oneError[2]};
                    index++;
                }
                // fileParamが'2'（添付データファイル）で、分割した最初のデータが「添付」の場合
                if (fileParam === '2' && oneError[0] === SystemConst.FILE_DIVISION.ATTACHMENT) {
                    // 「、」で分割した真ん中のデータを'errorLocation'に設定する
                    // 「、」で分割した後ろのデータを'message'に設定する
                    state.fileErrorItems[index] = {errorLocation: oneError[1], message: oneError[2]};
                    index++;
                }
            }
            // エラーのない表示が選択された場合
            if (state.fileErrorItems.length === 0) {
                state.fileErrorItems[index] = {errorLocation: '', message: MessageConst.S_NO_ERROR_MESSAGE};
            }

            return;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
         function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            commonRef,
            basicPondRef,
            attachmentPondRef,
            stepper,
            router,
            selectCreation,
            attachmentTreeFormat,
            excelFileFormat,
            updateBasicFiles,
            updateAttachmentFiles,
            nextStep,
            processingData,
            changeDialog,
            onResize
        };
    }
});
